import React from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingBag, Upload, BarChart3, LogOut, Store } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const NAV = [
    { to: '/brand', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/brand/orders', label: 'Orders', icon: ShoppingBag },
    { to: '/brand/products', label: 'Catalog', icon: Package },
    { to: '/brand/inventory', label: 'Inventory Upload', icon: Upload },
    { to: '/brand/analytics', label: 'Analytics', icon: BarChart3 },
];

export default function BrandLayout({ children }) {
    const { user, type, logout, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    if (loading) return <div className="min-h-screen flex items-center justify-center text-inorbit-blue">Loading…</div>;
    if (type !== 'brand') return <Navigate to="/brand/login" replace />;

    const brand = user.brand || {};

    return (
        <div className="min-h-screen flex bg-cream" data-testid="brand-layout">
            <aside className="w-64 bg-inorbit-blue-deeper text-white flex flex-col fixed h-screen" data-testid="brand-sidebar">
                <div className="px-6 py-7 border-b border-white/10">
                    <Link to="/brand" className="block" data-testid="brand-portal-home">
                        <div className="flex items-center gap-2">
                            <Store size={18} className="text-inorbit-yellow" strokeWidth={1.6} />
                            <div className="font-serif text-2xl text-white leading-none">Brand Portal</div>
                        </div>
                        <div className="text-[10px] tracking-[0.28em] text-inorbit-yellow font-semibold mt-2 uppercase">{brand.name || 'Tenant'}</div>
                    </Link>
                </div>
                <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                    {NAV.map((n) => {
                        const active = n.exact ? location.pathname === n.to : location.pathname.startsWith(n.to);
                        return (
                            <Link
                                key={n.to}
                                to={n.to}
                                data-testid={`brand-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors rounded-md ${active ? 'bg-white/10 text-inorbit-yellow' : 'text-white/70 hover:text-white hover:bg-white/5'}`}
                            >
                                <n.icon size={16} strokeWidth={1.5} />
                                {n.label}
                            </Link>
                        );
                    })}
                </nav>
                <div className="border-t border-white/10 p-4">
                    <div className="text-xs text-white/60 mb-3">
                        <div className="font-medium text-white">{user.name}</div>
                        <div className="capitalize">Brand Admin</div>
                    </div>
                    <button onClick={() => { logout(); navigate('/brand/login'); }} className="flex items-center gap-2 text-xs text-white/60 hover:text-inorbit-yellow" data-testid="brand-logout-btn">
                        <LogOut size={14} strokeWidth={1.5} /> Sign out
                    </button>
                    <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-white/40">
                        Powered by <span className="text-inorbit-yellow font-semibold">Fundle.ai</span>
                    </div>
                </div>
            </aside>
            <main className="flex-1 ml-64 min-w-0">{children}</main>
        </div>
    );
}
